import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@rudi/api/react';
import type { AIChatBackend, ChatModelOption } from './types';

export type ChatExecutor = 'codex' | 'claude_code' | 'api';
/** Executors every Rudi release with platform defaults offers. */
type BaseExecutor = 'codex' | 'api';

export const CHAT_BACKEND_LABELS: Record<AIChatBackend, string> = {
  codex: 'Codex CLI',
  claude_code: 'Claude Code CLI',
  api: 'API',
};
export const CHAT_BACKEND_ORDER: ChatExecutor[] = ['claude_code', 'codex', 'api'];
export interface ModelDefault {
  model: string;
  effort: string;
}

/** Rudi releases before Claude Code came back omit its entries. */
export type ExecutorRecord<T> = Record<BaseExecutor, T> & Partial<Record<'claude_code', T>>;

export interface PlatformChatDefaults {
  schema_version: 1;
  revision: number;
  ai_chat_backend: ChatExecutor;
  models: ExecutorRecord<ModelDefault>;
  /** Rerun a failed CLI AI chat turn on the API model. Rudi releases before it omit this. */
  codex_fallback?: boolean;
  /** The API model a failed CLI turn reruns on. Rudi releases before it omit this. */
  fallback?: ModelDefault;
  model_options: ExecutorRecord<ChatModelOption[]>;
  /** When Rudi last loaded OpenRouter's model list; null or absent when it has none. */
  openrouter_models_fetched_at?: string | null;
}

export interface UpdatePlatformChatDefaults {
  schema_version: 1;
  expected_revision: number;
  ai_chat_backend: ChatExecutor;
  /** Send claude_code only to a Rudi that returned it; older releases reject unknown fields. */
  models: ExecutorRecord<ModelDefault>;
  /** Send only to a Rudi that returned the field; older releases reject unknown fields. */
  codex_fallback?: boolean;
  /** Send only to a Rudi that returned the field; older releases reject unknown fields. */
  fallback?: ModelDefault;
}

/** The executors this Rudi's defaults offer, in display order. */
export function platformExecutors(defaults: PlatformChatDefaults): ChatExecutor[] {
  return CHAT_BACKEND_ORDER.filter(backend => defaults.model_options[backend] !== undefined);
}

export function chatDefaultsError(error: unknown): string {
  const data = (error as { response?: { data?: { error?: string; detail?: string } } })?.response
    ?.data;
  return (
    data?.error ??
    data?.detail ??
    (error instanceof Error ? error.message : 'Could not save chat defaults')
  );
}

function parseDefaults(data: PlatformChatDefaults): PlatformChatDefaults {
  const offered: ChatExecutor[] =
    data?.model_options?.claude_code === undefined && data?.models?.claude_code === undefined
      ? ['codex', 'api']
      : ['claude_code', 'codex', 'api'];
  if (
    data?.schema_version !== 1 ||
    !Number.isSafeInteger(data.revision) ||
    data.revision < 0 ||
    !offered.includes(data.ai_chat_backend) ||
    (data.codex_fallback !== undefined && typeof data.codex_fallback !== 'boolean') ||
    (data.fallback !== undefined &&
      (typeof data.fallback?.model !== 'string' ||
        data.fallback.model.length === 0 ||
        typeof data.fallback.effort !== 'string' ||
        data.fallback.effort.length === 0)) ||
    !offered.every(backend => {
      const model = data.models?.[backend];
      const options = data.model_options?.[backend];
      return (
        typeof model?.model === 'string' &&
        model.model.length > 0 &&
        typeof model.effort === 'string' &&
        model.effort.length > 0 &&
        Array.isArray(options) &&
        options.every(
          option =>
            typeof option?.model === 'string' &&
            typeof option.label === 'string' &&
            Array.isArray(option.efforts) &&
            option.efforts.every(effort => typeof effort === 'string' && effort.length > 0)
        )
      );
    })
  )
    throw new Error('This Rudi connection returned an unsupported chat defaults format.');
  return data;
}

export function usePlatformChatDefaults() {
  const api = useApiClient();
  return useQuery({
    queryKey: ['platform-chat-defaults'],
    queryFn: async () => {
      try {
        return parseDefaults((await api.get<PlatformChatDefaults>('/admin/chat-defaults')).data);
      } catch (error) {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 404 || status === 405 || status === 501) return null;
        throw error;
      }
    },
  });
}

export function useUpdatePlatformChatDefaults() {
  const api = useApiClient();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdatePlatformChatDefaults) => {
      const saved = parseDefaults(
        (await api.put<PlatformChatDefaults>('/admin/chat-defaults', payload)).data
      );
      if (
        saved.revision <= payload.expected_revision ||
        saved.ai_chat_backend !== payload.ai_chat_backend ||
        (payload.codex_fallback !== undefined && saved.codex_fallback !== payload.codex_fallback) ||
        (payload.fallback !== undefined &&
          (saved.fallback?.model !== payload.fallback.model ||
            saved.fallback?.effort !== payload.fallback.effort)) ||
        !CHAT_BACKEND_ORDER.every(backend => {
          const sent = payload.models[backend];
          return (
            sent === undefined ||
            (saved.models[backend]?.model === sent.model &&
              saved.models[backend]?.effort === sent.effort)
          );
        })
      )
        throw new Error('Rudi did not confirm these defaults. Reload before trying again.');
      return saved;
    },
    onSuccess: async saved => {
      client.setQueryData(['platform-chat-defaults'], saved);
      await Promise.all([
        client.invalidateQueries({ queryKey: ['tenant'] }),
        client.invalidateQueries({ queryKey: ['tenants'] }),
      ]);
    },
    onError: async error => {
      if ((error as { response?: { status?: number } })?.response?.status === 409) {
        await client.invalidateQueries({ queryKey: ['platform-chat-defaults'] });
      }
    },
  });
}

/**
 * Ask Rudi for OpenRouter's current model list. Rudi releases without the
 * parameter ignore it and answer with the list they hold.
 */
export function useRefreshOpenRouterModels() {
  const api = useApiClient();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      parseDefaults(
        (
          await api.get<PlatformChatDefaults>('/admin/chat-defaults', {
            params: { refreshModels: true },
          })
        ).data
      ),
    onSuccess: data => client.setQueryData(['platform-chat-defaults'], data),
  });
}

/** Where a team's executor or model comes from: the team, its tenant, or the platform default. */
export type TeamChatDefaultSource = 'team' | 'tenant' | 'default';

export interface TeamChatModel extends ModelDefault {
  source: TeamChatDefaultSource;
}

export interface TeamChatDefaults {
  ai_chat_backend: AIChatBackend;
  ai_chat_backend_source: TeamChatDefaultSource;
  inherited_ai_chat_backend: AIChatBackend;
  ai_chat_model: TeamChatModel | null;
  inherited_ai_chat_model: TeamChatModel | null;
  ai_chat_model_options: ChatModelOption[];
}

/** Omitted fields stay as they are; null clears the team's own setting. */
export interface UpdateTeamChatDefaults {
  ai_chat_backend?: ChatExecutor | null;
  ai_chat_model?: ModelDefault | null;
}

const BACKENDS: AIChatBackend[] = ['codex', 'claude_code', 'api'];
const SOURCES: TeamChatDefaultSource[] = ['team', 'tenant', 'default'];

function isTeamModel(value: unknown): value is TeamChatModel {
  const model = value as TeamChatModel | null;
  return (
    typeof model?.model === 'string' &&
    model.model.length > 0 &&
    typeof model.effort === 'string' &&
    SOURCES.includes(model.source)
  );
}

function parseTeamDefaults(data: TeamChatDefaults): TeamChatDefaults {
  if (
    !BACKENDS.includes(data?.ai_chat_backend) ||
    !SOURCES.includes(data.ai_chat_backend_source) ||
    !BACKENDS.includes(data.inherited_ai_chat_backend)
  )
    throw new Error('This Rudi connection returned an unsupported team chat defaults format.');
  const options = Array.isArray(data.ai_chat_model_options)
    ? data.ai_chat_model_options.filter(
        option =>
          typeof option?.model === 'string' &&
          typeof option.label === 'string' &&
          Array.isArray(option.efforts)
      )
    : [];
  return {
    ...data,
    ai_chat_model: isTeamModel(data.ai_chat_model) ? data.ai_chat_model : null,
    inherited_ai_chat_model: isTeamModel(data.inherited_ai_chat_model)
      ? data.inherited_ai_chat_model
      : null,
    ai_chat_model_options: options,
  };
}

/** True when this Rudi release has no team chat defaults route at all. */
function missingRoute(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { error?: unknown } } })
    ?.response;
  if (response?.status === 405 || response?.status === 501) return true;
  if (response?.status !== 404) return false;
  // Rudi answers an unknown tenant or team with {"error": ...}. A missing
  // route gets the framework's plain {"detail": "Not Found"} instead.
  return typeof response.data?.error !== 'string';
}

export function teamChatDefaultsKey(tenantId: string, teamId: string) {
  return ['team-chat-defaults', tenantId, teamId] as const;
}

function teamChatDefaultsPath(tenantId: string, teamId: string) {
  return `/admin/tenants/${tenantId}/teams/${teamId}/chat-defaults`;
}

export function useTeamChatDefaults(tenantId: string, teamId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: teamChatDefaultsKey(tenantId, teamId),
    retry: false,
    queryFn: async () => {
      try {
        return parseTeamDefaults(
          (await api.get<TeamChatDefaults>(teamChatDefaultsPath(tenantId, teamId))).data
        );
      } catch (error) {
        if (missingRoute(error)) return null;
        throw error;
      }
    },
  });
}

export function useUpdateTeamChatDefaults(tenantId: string, teamId: string) {
  const api = useApiClient();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateTeamChatDefaults) =>
      parseTeamDefaults(
        (await api.patch<TeamChatDefaults>(teamChatDefaultsPath(tenantId, teamId), payload)).data
      ),
    onSuccess: async saved => {
      client.setQueryData(teamChatDefaultsKey(tenantId, teamId), saved);
      await client.invalidateQueries({ queryKey: teamChatDefaultsKey(tenantId, teamId) });
    },
  });
}
